<template>
	<q-form class="row q-col-gutter-md justify-end" @submit="changeUserPassword">
		<div class="col-12 text-h6" :class="isMobile && !prop.is_obrigatory ? 'text-white' : ''">
			{{ $t('change_password') }}
		</div>

		<div class="col-12" v-if="!must_change_password">
			<q-input
				outlined
				bg-color="white"
				square
				dense
				hide-bottom-space
				v-model="data.current_password"
				:label="$t('current_password')"
				:type="data.isPwdthree ? 'password' : 'text'"
				:error="v$.current_password.$error"
				@input="v$.current_password.$touch"
			>
				<template v-slot:append>
					<q-icon
						:name="data.isPwdthree ? 'fal fa-eye-slash' : 'fal fa-eye'"
						class="cursor-pointer"
						@click="data.isPwdthree = !data.isPwdthree"
					/>
				</template>
			</q-input>
		</div>

		<div class="col-12">
			<q-input
				outlined
				bg-color="white"
				square
				dense
				v-model="data.new_password"
				:label="$t('new_password')"
				:type="data.isPwd ? 'password' : 'text'"
				:error="v$.new_password.$error"
				@input="v$.new_password.$touch"
			>
				<template v-slot:append>
					<q-icon
						:name="data.isPwd ? 'fal fa-eye-slash' : 'fal fa-eye'"
						class="cursor-pointer"
						@click="data.isPwd = !data.isPwd"
					/>
				</template>
				<template v-slot:hint>
					<span :class="isMobile && !is_obrigatory ? 'text-white' : ''">
						{{ passwordHint }}
					</span>
				</template>

				<template v-slot:error>
					{{ passwordHint }}
				</template>
			</q-input>
		</div>

		<div class="col-12">
			<q-input
				:class="isMobile ? 'q-pt-md' : ''"
				outlined
				bg-color="white"
				square
				dense
				hide-bottom-space
				v-model="data.confirm_password"
				:label="$t('confirm_password')"
				:type="data.isPwdtwo ? 'password' : 'text'"
				:error="v$.confirm_password.$error"
				@input="v$.confirm_password.$touch"
			>
				<template v-slot:append>
					<q-icon
						:name="data.isPwdtwo ? 'fal fa-eye-slash' : 'fal fa-eye'"
						class="cursor-pointer"
						@click="data.isPwdtwo = !data.isPwdtwo"
					/>
				</template>
			</q-input>
		</div>

		<div class="col-12 text-right" v-if="!isMobile">
			<q-btn
				:label="$t('logout')"
				class="q-mr-md"
				@click="logout()"
				color="accent"
				v-if="prop.is_obrigatory && !must_change_password"
			/>
			<q-btn
				:label="$t('cancel')"
				color="secondary"
				class="q-mr-md"
				@click="cancelResetPassword()"
				v-if="!prop.is_obrigatory"
			/>
			<q-btn :label="$t('change_password')" type="submit" color="primary" />
		</div>

		<div class="col-6" v-if="isMobile && !prop.is_obrigatory">
			<q-btn
				:label="$t('cancel')"
				color="accent"
				class="full-width"
				@click="cancelResetPassword()"
			/>
		</div>

		<div class="col-6" v-if="isMobile && prop.is_obrigatory && !must_change_password">
			<q-btn :label="$t('logout')" class="full-width" @click="logout()" color="accent" />
		</div>

		<div class="col-6" v-if="isMobile">
			<q-btn :label="$t('change_password')" class="full-width" type="submit" color="primary" />
		</div>
	</q-form>
</template>

<script setup>
import { computed, reactive, getCurrentInstance, ref } from 'vue'
import { axios } from 'src/boot/axios'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { required, requiredIf } from '@vuelidate/validators'
import { useQuasar } from 'quasar'
import useVuelidate from '@vuelidate/core'
import { Plugins } from '@capacitor/core'
const { Storage } = Plugins

const { ctx } = getCurrentInstance()
const router = useRouter()
const emit = defineEmits(['passwordChanged'])
const prop = defineProps(['is_obrigatory'])
const store = useStore()
const $q = useQuasar()

const must_change_password = computed(() => store.getters['common/must_change_password'])
const config = ref(null)

const data = reactive({
	confirm_password: '',
	current_password: '',
	error: false,
	new_password: '',
	success: false,
	isPwd: true,
	isPwdtwo: true,
	isPwdthree: true,
})

// const recaptcha = async () => {
// 	await recaptchaLoaded()
// 	const token = await executeRecaptcha('login')
// 	return token
// }

/**
 * * Consulta de configurações
 * É necessário realizar uma chamada para API de configuração para obter os valores de password_strong_level.
 * O valor do password_strong_level armazenado no estado com Vuex não reflete o valor configurado no painel.
 */
async function fetchConfig() {
	const res = await axios.get('/clients/config/')
	config.value = res.data
}
fetchConfig()

const passwordMatchRule = () => data.new_password === data.confirm_password

const validPassword = () => {
	var hardRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{6,})')
	var mediumRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})')
	var low = new RegExp('^(?=.{6,})')

	if (password_strong_level.value == 'hard') return hardRegex.test(data.new_password)
	else if (password_strong_level.value == 'medium') return mediumRegex.test(data.new_password)
	else return low.test(data.new_password)
}

const rules = {
	current_password: {
		required: requiredIf(() => !must_change_password.value),
	},
	new_password: { required, validPassword },
	confirm_password: { passwordMatchRule: passwordMatchRule },
}

const v$ = useVuelidate(rules, data, { $scope: false })

const password_strong_level = computed(() => {
	return config.value.register.password_strong_level
})

const passwordHint = computed(() => {
	if (password_strong_level.value === 'hard')
		return `${ctx.$t(
			'your_password_must_contain_at_least_characters_uppercase_letters_lowercase_letters_numbers_and_symbols',
			{ symbols: '(! @ # $% ˆ)' }
		)}`
	else if (password_strong_level.value === 'medium')
		return ctx.$t(
			'your_password_must_contain_at_least_characters_uppercase_letters_lowercase_letters_numbers'
		)
	else return ctx.$t('your_password_must_contain_at_least_characters')
})

const isMobile = computed(() => $q.platform.is.mobile)

const changeOldPassword = async () => {
	let fd = new FormData()
	fd.append('current_password', data.current_password)
	fd.append('new_password', data.new_password)
	fd.append('confirm_password', data.confirm_password)

	axios
		.post(`/etraining_profile/my_profile/change_user_password/`, fd)
		.then((response) => {
			$q.notify({
				message: ctx.$t('password_changed_successfully'),
				type: 'positive',
				icon: 'fal fa-save',
			})

			emit('passwordChanged', [])
			cancelResetPassword()
		})
		.catch((e) => {
			console.log(e)

			$q.notify({
				message: ctx.$t('error_changing_password_check_your_current_password_or_try_again_later'),
				type: 'negative',
				icon: 'fal fa-ban',
			})
		})
		.finally(() => {
			$q.loading.hide()
		})
}

const changeFirstPassword = () => {
	let fd = new FormData()
	fd.append('password', data.new_password)

	axios
		.post(`/etraining_login/change_first_access_password/`, fd)
		.then((response) => {
			$q.notify({
				message: ctx.$t('password_changed_successfully'),
				type: 'positive',
				icon: 'fal fa-save',
			})

			emit('passwordChanged', [])
			cancelResetPassword()
			store.dispatch('common/SET_MUST_CHANGE_PASSWORD', false)
		})
		.catch((e) => {
			console.log(e)

			$q.notify({
				message: ctx.$t('error_changing_password_check_your_current_password_or_try_again_later'),
				type: 'negative',
				icon: 'fal fa-ban',
			})
		})
		.finally(() => {
			$q.loading.hide()
		})
}

const changeUserPassword = () => {
	v$.value.$touch()
	if (!v$.value.$error) {
		$q.loading.show()

		if (must_change_password.value === true) {
			changeFirstPassword()
		} else {
			changeOldPassword()
		}
	}
}

const cancelResetPassword = () => {
	data.current_password = ''
	data.new_password = ''
	data.confirm_password = ''

	v$.value.$reset()
}

const logout = () => {
	// this.$zendesk.hide()
	store.dispatch('common/SET_ACCESS_TOKEN', ['', '', ''])
	setTimeout(() => {
		$q.loading.hide()
		router.replace({ name: 'login' })
	}, 700)
}
</script>

<style></style>
